import React, { useEffect, useCallback, useState, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Button, SubHeading } from '@leroy-merlin-br/backyard-react-ui'

import { dataLayer } from 'reactSite/Cart/services'

import { usePrevious } from 'reactUtils/hooks/use-previous'

import PriceInfo from 'checkout/components/Cart/Prices'
import { Quantity } from 'checkout/components/Quantity'
import { Actions } from 'checkout/redux/modules/crossServices'

import FullBadge from '../Icons/SquareBadge'
import TermsOfUse from '../TermsOfUse'
import ContentNotification from './ContentNotification'
import * as S from './styled'

const Content = ({
  skuId,
  productQuantity = 1,
  relatedId,
  productName,
  serviceBankName = null,
  id,
  serviceId,
  regionId,
  name,
  packaging = 1,
  packagingCount = 1,
  unit = 'un',
  price,
  isAvailable = false,
  isAdded = false,
  acceptanceTerms,
  errors = {}
}) => {
  const [showTerms, setShowTerms] = useState(false)
  const [serviceQuantity, setServiceQuantity] = useState(productQuantity)

  const previousServiceQuantity = usePrevious(serviceQuantity)

  const serviceDatalayerObject = useMemo(() => {
    return {
      serviceId,
      serviceName: name,
      price,
      category: serviceBankName,
      productName
    }
  }, [serviceId, name, price, serviceBankName, productName])

  const addServiceToDataLayer = useCallback((newQuantity) => {
    dataLayer.addService({
      ...serviceDatalayerObject,
      regionId,
      quantity: newQuantity
    })
  }, [serviceDatalayerObject, regionId])

  const removeServiceFromDataLayer = useCallback((newQuantity) => {
    dataLayer.removeService({
      ...serviceDatalayerObject,
      regionId,
      quantity: newQuantity
    })
  }, [serviceDatalayerObject, regionId])

  const updateServiceDatalayer = useCallback((newQuantity) => {
    if (newQuantity > previousServiceQuantity) {
      addServiceToDataLayer(newQuantity)
    }

    if (newQuantity < previousServiceQuantity) {
      removeServiceFromDataLayer(newQuantity)
    }
  }, [previousServiceQuantity, addServiceToDataLayer, removeServiceFromDataLayer])

  const dispatch = useDispatch()

  const deleteService = useCallback(() => {
    removeServiceFromDataLayer(productQuantity)
    dispatch(Actions.serviceDelete(id))
  }, [dispatch, id, productQuantity, removeServiceFromDataLayer])

  const dispatchPostQuantity = useCallback((value) => {
    dispatch(Actions.servicePostQuantity({
      value,
      skuId,
      productQuantity,
      id: serviceId,
      relatedId
    }))
  }, [dispatch, skuId, productQuantity, serviceId, relatedId])

  const handleInputChange = useCallback((value) => {
    if (!value || value < 0) {
      return
    }

    if (value > productQuantity) {
      setServiceQuantity(productQuantity)
      return
    }

    setServiceQuantity(value)
  }, [productQuantity])

  const updateServiceQuantity = useCallback(({ value }) => {
    const newServiceQuantity = serviceQuantity + value

    if (newServiceQuantity > 0 && newServiceQuantity !== serviceQuantity && newServiceQuantity <= productQuantity) {
      setServiceQuantity(newServiceQuantity)
    }

    if (newServiceQuantity > productQuantity) {
      setServiceQuantity(productQuantity)
    }
  }, [serviceQuantity, productQuantity])

  const showServiceQuantityWarning = useMemo(() => {
    return productQuantity > serviceQuantity && isAdded
  }, [productQuantity, serviceQuantity, isAdded])

  const changeShowTermsStatus = useCallback(() => setShowTerms(status => !status), [])

  useEffect(() => {
    setServiceQuantity(productQuantity)
  }, [productQuantity])

  useEffect(() => {
    const isEqualPreviousQuantity = serviceQuantity === previousServiceQuantity

    if (!isAdded || isEqualPreviousQuantity) {
      return
    }

    dispatchPostQuantity(serviceQuantity)
    updateServiceDatalayer(serviceQuantity)
  }, [isAdded, serviceQuantity, previousServiceQuantity, dispatchPostQuantity, updateServiceDatalayer])

  if (!isAvailable) {
    return null
  }

  return (
    <S.Content>
      <S.TakeTogether as="span" size="giga" noMargin>
        Aproveite e leve junto:
      </S.TakeTogether>
      <S.Container errors={errors}>
        <S.Badge>
          <FullBadge />
        </S.Badge>
        <S.Column>
          <S.ServiceTitleWrapper>
            <SubHeading align="center" noMargin>
              Serviço de Instalação
            </SubHeading>
          </S.ServiceTitleWrapper>
          <S.Row>
            <S.Column>
              <S.Row>
                <S.InstallationName size="mega" noMargin>
                  {name}
                </S.InstallationName>
                {
                  isAdded && <S.RemoveService onClick={deleteService}>
                    <i className="glyph glyph-x" />
                  </S.RemoveService>
                }
              </S.Row>
              <S.SellerName>
                <S.ForProduct as="span" size="kilo" noMargin>Para o produto: </S.ForProduct>
                <S.Description noMargin>{productName}</S.Description>
                <S.UnityFromSP>
                  {productQuantity} unidades / em São Paulo
                </S.UnityFromSP>
              </S.SellerName>
            </S.Column>
            {
              !isAdded &&
                <Button onClick={changeShowTermsStatus} variant="outline">
                  <S.ButtonText as="span" size="mega" noMargin>
                    Adicionar
                  </S.ButtonText>
                </Button>
            }
          </S.Row>
          <S.Footer isAdded={isAdded}>
            {
              isAdded && <S.QuantityWrapper>
                <Quantity
                  packaging={packaging}
                  packagingCount={serviceQuantity}
                  productMaxStock={productQuantity}
                  translatedUnit="un"
                  quantity={productQuantity}
                  updateQuantity={updateServiceQuantity}
                  useFormatQuantity={serviceQuantity}
                  handleInputChange={handleInputChange}
                />
              </S.QuantityWrapper>
            }
            <PriceInfo
              packaging={packaging}
              packagingCount={packagingCount}
              translatedUnit={unit}
              quantity={productQuantity}
              price={price}
              productQuantity={productQuantity}
              isAdded={isAdded}
            />
            {
              isAdded
                ? <S.ProductWrapper>
                    <S.ForProduct>Para o produto:</S.ForProduct>
                    <S.ProductDescription as="span" size="kilo" noMargin>{productName}</S.ProductDescription>
                    <S.Deadline as="span" size="kilo" noMargin>Previsão de instalação: <S.Deadline as="span" size="kilo" noMargin bold>a partir de 3 dias úteis após a entrega dos produtos</S.Deadline></S.Deadline>
                  </S.ProductWrapper>
                : <Button onClick={changeShowTermsStatus} variant="outline" data-add-services>
                    <S.ButtonText as="span" size="mega" noMargin>
                      Adicionar
                    </S.ButtonText>
                  </Button>
            }
          </S.Footer>
        </S.Column>
      </S.Container>
      <TermsOfUse
        acceptanceTerms={acceptanceTerms}
        serviceId={serviceId}
        name={name}
        showTerms={showTerms}
        productQuantity={productQuantity}
        onClose={changeShowTermsStatus}
        onClickCallBack={(value) => {
          dispatchPostQuantity(value)
          addServiceToDataLayer(productQuantity)
        }}
      />
      <ContentNotification itemName="serviço de instalação" isQuantityNotEqual={showServiceQuantityWarning} errors={errors} />
    </S.Content>
  )
}

export default Content
